// frontend/src/pages/SetPassword.js
// ============================================================
// Token-based password setup for Path A students
// (parent created their profile, child needs to set a password)
// ============================================================

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_URL } from './config';
import { useAuth } from './AuthContext';

export default function SetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [password, setPassword]       = useState('');
  const [confirmPw, setConfirmPw]     = useState('');
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState(false);
  const [loading, setLoading]         = useState(false);
    const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Missing token. Please use the link from your email.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPw) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
            const res = await fetch(`${API_URL}/api/auth/set-initial-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Failed to set password. The link may have expired.');
        setLoading(false);
        return;
      }

            // Auto-login via AuthContext (stores under correct key + validates)
      if (data.token && data.user) {
        login(data.token, data.user);
      }

      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError('Network error — please try again.');
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <img src="/YSF_logo_horizontal_navy.png" alt="Your Scholarship Finder" style={styles.logo} />
          <h1 style={styles.title}>Invalid Link</h1>
          <p style={styles.subtitle}>
            This link is missing a token. Please use the link from the email we sent you,
            or go to the login page and click "Forgot your password?" to get a new one.
          </p>
          <button onClick={() => navigate('/login')} style={styles.primaryBtn}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <img src="/YSF_logo_horizontal_navy.png" alt="Your Scholarship Finder" style={styles.logo} />
        <h1 style={styles.title}>Create Your Password</h1>
        <p style={styles.subtitle}>
          Your parent set up your scholarship profile — your matches are ready!
          Just create a password below so you can log in and see them.
        </p>

        {error && <div style={styles.error}>{error}</div>}

        {success ? (
          <div style={styles.success}>
            Password set! Redirecting you to login…
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                style={styles.input}
                placeholder="At least 8 characters"
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Confirm Password</label>
              <input
                type="password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                required
                style={styles.input}
                placeholder="Type it again"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              style={{ ...styles.primaryBtn, opacity: loading ? 0.6 : 1 }}
            >
              {loading ? 'Setting password…' : 'Create My Password →'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#F3F4F6',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    padding: '20px',
  },
  card: {
    background: '#fff',
    borderRadius: '12px',
    padding: '40px',
    maxWidth: '440px',
    width: '100%',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  },
  logo: {
    maxWidth: '220px',
    height: 'auto',
    marginBottom: '20px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6B7280',
    marginBottom: '24px',
    lineHeight: '1.5',
  },
  error: {
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: '8px',
    padding: '12px',
    color: '#DC2626',
    fontSize: '13px',
    marginBottom: '16px',
  },
  success: {
    background: '#F0FDF4',
    border: '1px solid #BBF7D0',
    borderRadius: '8px',
    padding: '16px',
    color: '#16A34A',
    fontSize: '15px',
    fontWeight: '600',
    textAlign: 'center',
  },
  form: {},
  field: {
    marginBottom: '14px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  primaryBtn: {
    width: '100%',
    padding: '12px',
    background: '#374151',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
  },
};